import { Component, Output, EventEmitter, Input, OnChanges, SimpleChanges, ViewChild, TemplateRef, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators, FormControl, AbstractControl } from '@angular/forms';
import { NgbAccordionModule, NgbNavModule, NgbToastModule, NgbTooltipModule, NgbDropdownModule, NgbModal, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { CdkScrollable, ScrollingModule } from '@angular/cdk/scrolling';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { TablesService } from '../../core/service/table.service';
import { SchemaService, SchemaResponse } from '../../core/service/schema.service';
import { UUID } from '../../pages/aichat/aichat.model';
import { FileUpload, SchemaSettings } from '../../layout/shell/shell.model';
import { ToastService } from '../../toast/toast.service';
import { UserService } from '../../core/service/user.service';
import { CardEditComponent } from './card-edit/card-edit.component';
import { DocumentQaInfoComponent } from '../../layout/shell/document-qa-info/document-qa-info.component';

interface PropertyDefinition {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'null';
  description?: string;
  example?: string;
  options?: string[];
  presence: 'occasional' | 'common' | 'rare';
  pattern?: string;
  format?: string;
  enum?: string[];
  required?: boolean;
}

interface SheetDefinition {
  name: string;
  properties: PropertyDefinition[];
  multiple_rows?: boolean;
  isSheetNameEdit?: boolean;
  editedSheetName?: string;
  showDropDown?: boolean;
}

interface SheetFormGroup {
  get(path: string): AbstractControl | null;
  patchValue(value: any): void;
  value: {
    name: string;
    properties: any[];
    multiple_rows: boolean;
    isSheetNameEdit: boolean;
    editedSheetName: string;
    showDropDown: boolean;
  };
}

interface ColumnDefinition {
  name: string;
  type: string;
  description?: string;
  example?: string;
  required?: boolean;
  options?: string[];
  presence: 'occasional' | 'common' | 'rare';
}

@Component({
  selector: 'app-schema-builder',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    ReactiveFormsModule, 
    NgbAccordionModule, 
    NgbNavModule, 
    NgbToastModule, 
    NgbTooltipModule, 
    NgbDropdownModule, 
    DragDropModule, 
    ScrollingModule
  ],
  templateUrl: './schema-builder.component.html',
  styleUrls: ['./schema-builder.component.scss']
})
export class SchemaBuilderComponent implements OnChanges, OnInit, OnDestroy {
  private isCardEditModalOpen = false;
  @ViewChild('resetModal') resetModal!: TemplateRef<any>;
  @ViewChild('saveTemplateModal') saveTemplateModal!: TemplateRef<any>;
  @ViewChild('deleteConfirmModal') deleteConfirmModal!: TemplateRef<any>;
  @ViewChild('templateDropdown') templateDropdown!: NgbDropdown;
  @ViewChild('scrollableContainer') scrollableContainer!: CdkScrollable;
  schemaForm: FormGroup;
  saveTemplateForm: FormGroup;
  generatedSchema: any = null;
  private schemaSheetsConfig?: any;
  activeTab = 0;
  activeColumnTabs: { [key: number]: number } = {};
  showToast = false;
  toastType: 'success' | 'error' = 'success';
  toastMessage = '';
  isSaving = false;
  _isFormValid = false;
  @Input() jsonSchema: string = '';
  @Input() schema_id: string = '';
  @Input() schema_name: string = '';
  @Input() tableId?: UUID;
  /** When true (shell is loading), the QA info button is disabled. */
  @Input() shellLoading = false;
  @Input() files: FileUpload[] = [];
  @Input() schemaSettings?: SchemaSettings;
  @Input() schemaConfig?: any;
  /** When the parent increments this (e.g. after Structure API succeeds), we reset schema changed state. */
  @Input() structureSuccessTrigger = 0;
  /** Whether any file has been structured (passed from shell; used for mismatch warning and drag-drop). */
  @Input() hasStructuredFiles = false;
  @Output() jsonSchemaChanged = new EventEmitter<string>();
  @Output() formValid = new EventEmitter<boolean>(); // Whether or not the form is valid, so we can show the Structure button
  @Output() schemaChanged = new EventEmitter<boolean>();
  @Output() applyAndResolve = new EventEmitter<void>();
  private formChangeSubscription?: Subscription;
  private sheetChangeSubscriptions: Subscription[] = []; // Track individual sheet subscriptions
  private readonly autoSaveDebounceTime = 1000; // Auto-save after 1 second of inactivity
  private isInitializing = false; // Flag for initial setup to prevent emitting form changes on init
  private hasInitialized = false; // Track if we've completed initial setup
  private lastReceivedSchema: string = ''; // Track last received schema to prevent loops
  private previousSchemaSettingsMultipleRows: boolean | undefined; // Track top-level multiple_rows to apply to all sheets when it changes
  /** True when the loaded schema cannot be displayed in the builder (e.g. oneOf / non-platform format). Schema is still saved; user can edit in JSON view. */
  schemaFormatUnsupported = false;

  // Template management properties
  selectedTemplate: string = '';
  availableSchemas: SchemaResponse[] = [];

  // Properties for delete confirmation
  deleteType: string = 'Structure';
  deleteItemName: string = '';
  deleteItemId: string = '';
  deleteCallback?: () => void;

  editingTemplateId: string = '';
  editingTemplateName: string = '';
  private isSavingTemplate: boolean = false;

  private readonly DEFAULT_TITLE = 'Data Template';
  private readonly DEFAULT_DESCRIPTION = 'A structured template for organizing and managing data';
  
  private autoScrollInterval: any = null;
  private readonly SCROLL_SPEED = 5;
  private readonly SCROLL_ZONE = 50;


  showTextInputForAI = false;
  textInputForAI: string = '';
  hasSchemaChanged = false;

  set isFormValid(valid: boolean) {
    this._isFormValid = valid;
    this.formValid.emit(valid);
  }
  get isFormValid() {
    return this._isFormValid;
  }

  get isDragDropEnabled(): boolean {
    return !this.hasStructuredFiles;
  }

  get promptControl(): FormControl {
    return this.schemaForm.get('prompt') as FormControl;
  }

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private tablesService: TablesService,
    private schemaService: SchemaService,
    private toastService: ToastService,
    private userService: UserService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.schemaForm = this.fb.group({
      title: [this.DEFAULT_TITLE],
      description: [this.DEFAULT_DESCRIPTION],
      spreadsheet_name: [{ "type": "string" }],
      sheets: this.fb.array([]),
      prompt: ['']
    });

    this.saveTemplateForm = this.fb.group({
      name: ['', Validators.required]
    });

    // Don't add default sheet here - wait for proper initialization
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['jsonSchema']) {
      if (changes['jsonSchema'].currentValue) {
        try {
          const schemaStr = changes['jsonSchema'].currentValue as string;

          // Skip if this is the same schema we just processed
          if (schemaStr === this.lastReceivedSchema) {
            return;
          }

          // Update our tracking
          this.lastReceivedSchema = schemaStr;

          const parsed = JSON.parse(schemaStr);
          const schema = parsed?.json_schema ?? parsed;
          this.initializeFormFromSchema(schema);
        } catch (e) {
          console.error('Failed to parse JSON schema:', e);
          this.toastService.showError('Warning', 'Invalid JSON schema detected. Initializing with empty template.');
          this.initializeEmptyForm();
        }
      } else {
        this.initializeEmptyForm();
      }
    }

    if (changes['schema_id'] && !changes['schema_id'].isFirstChange()) {
      const newSchemaId = changes['schema_id'].currentValue;
      if (newSchemaId !== this.selectedTemplate) {
        this.selectedTemplate = newSchemaId;
      }
    }


    if (changes['schemaSettings'] && !changes['schemaSettings'].isFirstChange() && this.hasInitialized && this.schemaSettings) {
      const newValue = this.schemaSettings.multiple_rows;
      if (this.previousSchemaSettingsMultipleRows !== newValue) {
        this.previousSchemaSettingsMultipleRows = newValue;
        this.applyTopLevelMultipleRowsToAllSheets();
      }
    }

    if (changes['structureSuccessTrigger'] && !changes['structureSuccessTrigger'].firstChange) {
      const trigger = changes['structureSuccessTrigger'].currentValue as number;
      if (trigger > 0) {
        this.resetSchemaChangedState();
      }
    }
  }

  private resetSchemaChangedState(): void {
    this.hasSchemaChanged = false;
    this.schemaChanged.emit(false);
  }

  private markSchemaChanged(): void {
    this.hasSchemaChanged = true;
    this.schemaChanged.emit(true);
  }

  ngOnInit() {

    this.loadAvailableSchemas();
    
    if (this.schema_id) {
      this.selectedTemplate = this.schema_id;
    }
    
    // If we start with no schema, initialize the empty form
    if (!this.jsonSchema) {
      this.initializeEmptyForm();
    }
  }

  ngOnDestroy() {
    this.clearFormChangeSubscriptions();
    this.stopAutoScroll();
  }

  onApplyAndResolveClick() {
    this.applyAndResolve.emit();
  }

  private initializeFormFromSchema(schema: any) {
    this.isInitializing = true;
    this.hasInitialized = false;
    
    // Clear any existing change subscriptions
    this.clearFormChangeSubscriptions();
    
    // Populate the form
    this.populateFormFromSchema(schema);
    
    // Set up change tracking AFTER population is complete
    setTimeout(() => {
      this.setupFormChangeTracking();
      this.isInitializing = false;
      this.hasInitialized = true;
      this.previousSchemaSettingsMultipleRows = this.schemaSettings?.multiple_rows;
      this.generatedSchema = schema;
    }, 100);
  }

  private initializeEmptyForm() {
    this.schemaFormatUnsupported = false;
    this.isInitializing = true;
    this.hasInitialized = false;
    
    // Clear any existing change subscriptions
    this.clearFormChangeSubscriptions();
    
    // Reset and add default sheet
    this.resetForm();
    this.addSheet();
    
    // Set up change tracking AFTER setup is complete
    setTimeout(() => {
      this.setupFormChangeTracking();
      this.isInitializing = false;
      this.hasInitialized = true;
      this.previousSchemaSettingsMultipleRows = this.schemaSettings?.multiple_rows;
    }, 100);
    
    this.lastReceivedSchema = "";
    this.isFormValid = false;
  }

  private setupFormChangeTracking() {
    // Only set up tracking if we're not initializing and have completed initial setup
    if (this.isInitializing || !this.hasInitialized) {
      return;
    }

    // Main form change subscription
    this.formChangeSubscription = this.schemaForm.valueChanges
      .pipe(
        debounceTime(this.autoSaveDebounceTime),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe(() => {
        if (this.schemaForm.valid && !this.isInitializing) {
          this.handleFormChange();
        }
      });

    // Set up tracking for each sheet
    this.setupSheetChangeTracking();
  }

  private setupSheetChangeTracking() {
    // Clear existing sheet subscriptions
    this.sheetChangeSubscriptions.forEach(sub => sub.unsubscribe());
    this.sheetChangeSubscriptions = [];

    // Set up tracking for each sheet
    for (let i = 0; i < this.sheets.length; i++) {
      this.trackSheetPropertyChanges(i);
    }
  }

  private trackSheetPropertyChanges(sheetIndex: number) {
    const propertiesArray = this.getSheetProperties(sheetIndex);
    const subscription = propertiesArray.valueChanges
      .pipe(debounceTime(this.autoSaveDebounceTime))
      .subscribe(() => {
        if (this.schemaForm.valid && !this.isInitializing && this.hasInitialized) {
          this.handleFormChange();
        }
      });
    
    this.sheetChangeSubscriptions.push(subscription);
  }

  private handleFormChange() {
    this.isSaving = true;
    if (!this.hasSchemaChanged) {
      this.markSchemaChanged();
    }
    this.saveAndGenerate();
    setTimeout(() => this.isSaving = false, 500);
  }

  private clearFormChangeSubscriptions() {
    if (this.formChangeSubscription) {
      this.formChangeSubscription.unsubscribe();
      this.formChangeSubscription = undefined;
    }
    
    this.sheetChangeSubscriptions.forEach(sub => sub.unsubscribe());
    this.sheetChangeSubscriptions = [];
  }

  /** Returns true if the schema has the platform shape (sheets.items array of sheet objects with sheet_name.const and rows.items.$ref). */
  private isPlatformSchema(schema: any): boolean {
    if (!schema?.properties?.sheets?.items) return false;
    const items = schema.properties.sheets.items;
    if (Array.isArray(items)) {
      const first = items[0];
      return !!(
        first &&
        typeof first === 'object' &&
        first.properties?.sheet_name?.const != null &&
        (first.properties?.rows?.items?.['$ref'] != null || first.properties?.rows?.items?.properties != null)
      );
    }
    if (items && typeof items === 'object' && Array.isArray(items.oneOf)) {
      return false;
    }
    return false;
  }

  private populateFormFromSchema(schema: any) {
    this.resetForm();
    this.schemaFormatUnsupported = false;

    if (!schema) {
      this.addSheet();
      return;
    }

    if (!this.isPlatformSchema(schema)) {
      this.schemaFormatUnsupported = true;
      this.addSheet();
      return;
    }

    try {
      this.schemaForm.patchValue({
        title: schema.title || this.DEFAULT_TITLE,
        description: schema.description || this.DEFAULT_DESCRIPTION,
        spreadsheet_name: schema.properties?.spreadsheet_name || { "type": "string" }
      });

      if (schema.$defs && schema.properties?.sheets?.items) {
        schema.properties.sheets.items.forEach((sheetDef: any) => {
          try {
            const sheetName = sheetDef.properties?.sheet_name?.const || 'Unnamed Sheet';

            let properties: any = {};
            let requiredFields: string[] = [];
            if (sheetDef.properties?.rows?.items?.["$ref"]) {
              const refKey = sheetDef.properties.rows.items["$ref"].split("/").pop();
              const def = schema.$defs[refKey] || {};
              properties = def.properties || {};
              requiredFields = Array.isArray(def.required) ? def.required : [];
            } else if (sheetDef.properties?.rows?.items?.properties) {
              properties = sheetDef.properties.rows.items.properties;
            }

            const sheetMultipleRows = this.schemaConfig?.configuration?.sheets?.[sheetName]?.configuration?.multiple_rows ?? false;
            const sheetGroup = this.fb.group({
              name: [sheetName, Validators.required],
              properties: this.fb.array([]),
              multiple_rows: [sheetMultipleRows],
              isSheetNameEdit: [false],
              editedSheetName: [''],
              showDropDown: [false]
            }) as SheetFormGroup;

            const propertiesArray = sheetGroup.get('properties') as FormArray;

            if (properties && typeof properties === 'object') {
              Object.entries(properties).forEach(([propName, propDef]: [string, any]) => {
                try {
                  const def = (propDef && typeof propDef === 'object') ? propDef : {};
                  const isRequired = requiredFields.includes(propName);
                  const enumValues = Array.isArray(def.enum) ? def.enum : [];
                  const hasEnum = enumValues.length > 0;
                  const rawType = Array.isArray(def.type)
                    ? def.type.find((t: string) => t !== 'null') ?? 'string'
                    : def.type;
                  const fieldType = rawType || 'string';
                  const exampleValue = !hasEnum && Array.isArray(def.examples) && def.examples.length
                    ? def.examples[0]
                    : '';
                  const presenceFromConfig =
                    this.schemaConfig?.configuration?.sheets?.[sheetName]?.fields?.[propName]?.configuration?.frequency;
                  const validPresences = ['occasional', 'common', 'rare'];
                  const rawPresence = presenceFromConfig || def.presence;
                  const presence = validPresences.includes(rawPresence) ? rawPresence : 'occasional';
                  const columnData: any = {
                    name: propName || 'Unnamed Column',
                    type: fieldType,
                    description: def.description || '',
                    required: isRequired,
                    presence
                  };
                  if (hasEnum) {
                    columnData.options = enumValues;
                  } else if (exampleValue) {
                    columnData.example = exampleValue;
                  }
                  propertiesArray.push(this.createColumnFormGroup(columnData));
                } catch (propError) {
                  console.warn('Error processing property:', propName, propError);
                }
              });
            }

            this.sheets.push(sheetGroup);
          } catch (sheetError) {
            console.warn('Error processing sheet definition:', sheetError);
          }
        });
      }

      if (this.sheets.length === 0) {
        this.addSheet();
      }

      this.activeTab = this.sheets.length > 0 ? 0 : 0;
    } catch (error) {
      console.warn('Error populating form from schema (schema may use a non-platform format):', error);
      this.schemaFormatUnsupported = true;
      if (this.sheets.length === 0) {
        this.addSheet();
      }
      this.activeTab = 0;
    }
  }

  get sheets() {
    return this.schemaForm.get('sheets') as FormArray;
  }

  addSheet() {
    const sheetIndex = this.sheets.length + 1;
    const sheetGroup = this.fb.group({
      name: [`Sheet ${sheetIndex}`, Validators.required],
      properties: this.fb.array([]),
      multiple_rows: [false],
      isSheetNameEdit: [false],
      editedSheetName: [''],
      showDropDown: [false]
    });

    this.sheets.push(sheetGroup);
    this.activeTab = this.sheets.length - 1;

    // Only track changes if we're not initializing
    if (!this.isInitializing && this.hasInitialized) {
      this.trackSheetPropertyChanges(this.sheets.length - 1);
    }
  }

  removeSheet(index: number) {
    // Prevent removing the last sheet
    if (this.sheets.length <= 1) {
      this.toastService.showError('Error', 'Cannot remove the last sheet. At least one sheet must exist.');
      return;
    }

    const currentActiveTab = this.activeTab;
    
    this.sheets.removeAt(index);

    if (this.sheets.length === 0) {
      this.addSheet();
      this.activeTab = 0;
    } else if (currentActiveTab >= this.sheets.length) {
      this.activeTab = this.sheets.length - 1;
    } else if (currentActiveTab === index) {
      this.activeTab = Math.min(currentActiveTab, this.sheets.length - 1);
    }
    else if (index < currentActiveTab) {
      this.activeTab = currentActiveTab - 1;
    }

    setTimeout(() => {
      this.saveAndGenerate();
    }, 100);
  }

  getSheetProperties(sheetIndex: number): FormArray {
    const sheet = this.sheets.at(sheetIndex);
    if (!sheet) {
      console.error('Sheet not found at index:', sheetIndex, 'Total sheets:', this.sheets.length);
      return this.fb.array([]);
    }
    const properties = sheet.get('properties');
    if (!properties) {
      console.error('Properties not found for sheet at index:', sheetIndex);
      return this.fb.array([]);
    }
    return properties as FormArray;
  }

  safeGetSheetProperties(sheetIndex: number): FormArray {
    try {
      if (sheetIndex < 0 || sheetIndex >= this.sheets.length) {
        return this.fb.array([]);
      }
      return this.getSheetProperties(sheetIndex);
    } catch (error) {
      console.error('Error getting sheet properties for index:', sheetIndex, error);
      return this.fb.array([]);
    }
  }

  saveAndGenerate() {
    // Don't save/generate during initialization
    if (this.isInitializing) {
      return;
    }

    if (this.sheets.length === 0) {
      this.isFormValid = false;
      return;
    }

    const currentActiveTab = this.activeTab;

    const allSheetsEmpty = this.sheets.controls.every((sheet: AbstractControl) => {
      const properties = sheet.get('properties') as FormArray;
      return properties.length === 0;
    });

    if (allSheetsEmpty && this.hasInitialized && !this.isInitializing) {
      console.log('All sheets are empty, but allowing form to remain valid for column addition');
    }

    if (this.schemaForm.valid) {
      const hasValidContent = this.sheets.controls.some((sheet: AbstractControl) => {
        const properties = sheet.get('properties') as FormArray;
        return properties.length > 0;
      });

      if (hasValidContent) {
        this.isFormValid = true;
        const formValue = this.schemaForm.value;
        const schema: any = {
        $schema: "http://json-schema.org/draft-07/schema#",
        title: this.schemaForm.get('title')?.value || this.DEFAULT_TITLE,
        description: this.schemaForm.get('description')?.value || this.DEFAULT_DESCRIPTION,
        type: "object",
        properties: {
          spreadsheet_name: this.schemaForm.get('spreadsheet_name')?.value || { "type": "string" },
          sheets: {
            type: "array",
            items: []
          }
        },
        required: ["spreadsheet_name", "sheets"],
        additionalProperties: false,
        $defs: {}
      };

      const sheetsConfig: any = {};
      formValue.sheets.forEach((sheet: SheetDefinition) => {
        const sheetProperties: any = {};
        const sheetRequired: string[] = [];
        const sheetFieldsConfig: any = {};

        sheet.properties.forEach((prop: PropertyDefinition) => {
          const frequency = prop.presence || 'occasional';
          const hasOptions = Array.isArray(prop.options) && prop.options.length > 0;
          sheetProperties[prop.name] = {
            type: prop.type || 'string'
          };
          if (prop.description) sheetProperties[prop.name].description = prop.description;
          if (!hasOptions && prop.example) {
            sheetProperties[prop.name].examples = [prop.example];
          }
          if (prop.required) sheetRequired.push(prop.name);
          if (hasOptions) {
            sheetProperties[prop.name].enum = prop.options;
          }
          sheetFieldsConfig[prop.name] = {
            configuration: {
              frequency
            }
          };
        });
        const sheetMultipleRows = sheet.multiple_rows ?? false;
        sheetsConfig[sheet.name] = {
          configuration: {
            multiple_rows: sheetMultipleRows
          },
          fields: sheetFieldsConfig
        };

        const defName = sheet.name.replace(/\s+/g, '');
        schema.$defs[defName] = {
          type: "object",
          properties: sheetProperties,
          required: sheetRequired,
          additionalProperties: false
        };

        const sheetSchema: any = {
          type: "object",
          properties: {
            sheet_name: { const: sheet.name },
            rows: {
              type: "array",
              items: { $ref: `#/$defs/${defName}` }
            },
            mode: {
              type: 'object',
              properties: {
                type: { const: 'add' }
              },
              required: ['type']
            }
          },
          additionalProperties: false,
          required: ['sheet_name', 'rows', 'mode']
        };

        schema.properties.sheets.items.push(sheetSchema);
      });

      this.generatedSchema = schema;
      this.schemaSheetsConfig = sheetsConfig;
      const formattedSchema = JSON.stringify(schema, null, 2);

      // Validate the generated schema
      if (!this.isValidSchema(schema)) {
        console.error('Generated schema is invalid:', schema);
        this.isFormValid = false;
        return;
      }

      console.log('Generated valid schema:', schema);

      // Only emit if this is a real change (not during initialization)
      if (this.hasInitialized && formattedSchema !== this.lastReceivedSchema) {
        this.lastReceivedSchema = formattedSchema;
        this.jsonSchemaChanged.emit(formattedSchema);
        
        // Save to database if schema was manually modified (=not incoming)
        if (this.tableId && this.generatedSchema) {
          this.sendSchemaToBackend();
        }
      }

        this.activeTab = currentActiveTab;
      } else {
        this.isFormValid = true;
        console.log('Form valid but no content to generate schema');
      }
    } else {
      this.isFormValid = false;
    }
  }

  addColumn(columnData: ColumnDefinition, sheet?: AbstractControl): void {
    const targetSheet = sheet ?? this.sheets.at(this.activeTab);
    if (!targetSheet) {
      console.error('No sheet found for addColumn.');
      return;
    }

    const sheetName = targetSheet.get('name')?.value;
    const sheetIndex = this.sheets.controls.indexOf(targetSheet);
    const sheetProperties = this.getSheetProperties(sheetIndex === -1 ? this.activeTab : sheetIndex);

    if (this.schema_id && sheetName) {
      this.schemaService.addColumn(this.schema_id, sheetName, columnData).subscribe({
        next: () => {
          const columnGroup = this.createColumnFormGroup(columnData);
          sheetProperties.push(columnGroup);
          this.markSchemaChanged();
          this.saveAndGenerate();
          this.toastService.show('info', 'Column added successfully', {
            classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
          });
        },
        error: () => {
          this.toastService.showError('Error', 'Failed to add column');
        }
      });
    } else {
      const columnGroup = this.createColumnFormGroup(columnData);
      sheetProperties.push(columnGroup);
      this.markSchemaChanged();
      this.saveAndGenerate();
    }
  }

  toggleColumnEdit(sheetIndex: number, columnIndex: number) {
    if (sheetIndex < 0 || sheetIndex >= this.sheets.length) {
      console.error('Invalid sheetIndex for toggleColumnEdit:', sheetIndex);
      return;
    }
    
    const properties = this.getSheetProperties(sheetIndex);
    if (columnIndex < 0 || columnIndex >= properties.length) {
      console.error('Invalid columnIndex for toggleColumnEdit:', columnIndex);
      return;
    }
    
    const column = properties.at(columnIndex);
    if (!column) {
      console.error('Column not found at index:', columnIndex);
      return;
    }
    
    const currentValue = column.get('isEditing')?.value;
    const sheet = this.sheets.at(sheetIndex);

    if (!currentValue) {
      this.openCardEditModal(sheet, column);
      return;
    }

    column.patchValue({ isEditing: !currentValue });

    if (currentValue) {
      if (column.invalid) {
        column.markAllAsTouched();
        this.toastService.showError('Error', 'Please fill in the required fields');
        column.patchValue({ isEditing: true }); // Keep in edit mode
        return;
      }

      const sheetName = sheet.get('name')?.value;
      const oldColumnName = column.get('oldName')?.value || column.get('name')?.value;

      const columnData: ColumnDefinition = {
        name: column.get('name')?.value,
        type: column.get('type')?.value,
        description: column.get('description')?.value,
        example: column.get('example')?.value,
        required: column.get('required')?.value,
        options: column.get('options')?.value ?? [],
        presence: column.get('presence')?.value ?? 'occasional'
      };

      if (this.schema_id && sheetName) {
        this.schemaService.updateColumn(this.schema_id, sheetName, oldColumnName, columnData).subscribe({
          next: () => {
            this.saveAndGenerate();
            this.toastService.show('info', 'Column updated successfully', {
              classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
            });
          },
          error: (error) => {
            this.toastService.showError('Error', 'Failed to update column');
          }
        });
      } else {
        this.saveAndGenerate();
      }
    }
  }

  private createColumnFormGroup(data: any = {}) {
    const group = this.fb.group({
      name: [data.name || '', Validators.required],
      type: [data.type || 'string', Validators.required],
      description: [data.description || ''],
      isEditing: [data.isEditing || false],
      required: [data.required || false],
      oldName: [data.oldName || ''],
      presence: [data.presence || 'occasional', Validators.required]
    });
    if (Array.isArray(data.options) && data.options.length > 0) {
      (group as FormGroup<any>).addControl('options', new FormControl(data.options));
    }
    if (data.example !== undefined && data.example !== null && data.example !== '') {
      (group as FormGroup<any>).addControl('example', new FormControl(data.example));
    }
    return group;
  }

  removeSheetProperty(sheetIndex: number, propertyIndex: number) {
    const sheet = this.sheets.at(sheetIndex);
    const properties = this.getSheetProperties(sheetIndex);
    const columnName = properties.at(propertyIndex).get('name')?.value;
    const sheetName = sheet.get('name')?.value;

    if (this.schema_id && columnName && sheetName) {
      this.schemaService.deleteColumn(this.schema_id, sheetName, columnName).subscribe({
        next: () => {
          properties.removeAt(propertyIndex);
          this.markSchemaChanged();
          this.saveAndGenerate();
          this.toastService.show('info', 'Column deleted successfully', {
            classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
          });
        },
        error: (error) => {
          this.toastService.showError('Error', 'Failed to delete column');
        }
      });
    } else {
      properties.removeAt(propertyIndex);
      this.markSchemaChanged();
      this.saveAndGenerate();
    }
  }

  resetForm() {
    this.schemaForm.reset();
    while (this.sheets.length) {
      this.sheets.removeAt(0);
    }
    this.generatedSchema = null;
  }

  editSheetName(sheet: AbstractControl) {
    if (sheet instanceof FormGroup) {
      sheet.patchValue({
        isSheetNameEdit: true
      });
      setTimeout(() => {
        const input = document.querySelector('input.form-control-sm') as HTMLInputElement;
        if (input) {
          input.focus();
          input.select();
        }
      }, 0);
    }
  }

  updateSheetName(event: Event, sheet: AbstractControl) {
    if (sheet instanceof FormGroup) {
      const input = event.target as HTMLInputElement;
      sheet.patchValue({
        name: input.value
      });
    }
  }

  stopEditingSheet(sheet: AbstractControl) {
    if (sheet instanceof FormGroup) {
      sheet.patchValue({
        isSheetNameEdit: false
      });
    }
  }

  confirmReset(content: any) {
    this.modalService.open(content, { centered: true }).result.then(
      (result) => {
        if (result === 'reset') {
          this.initializeEmptyForm();
        }
      },
      () => {}
    );
  }

  sendSchemaToBackend() {
    if (!this.tableId || !this.generatedSchema) {
      console.warn('Cannot send schema to backend:', { tableId: this.tableId, hasSchema: !!this.generatedSchema });
      return;
    }

    console.log('Sending schema to backend:', { tableId: this.tableId, schema: this.generatedSchema, schemaId: this.schema_id });

    this.tablesService.addSchemaToDataspace(
      this.generatedSchema,
      this.tableId,
      this.schema_id || undefined,
      this.schemaSettings,
      this.schemaSheetsConfig
    )
      .pipe(
        switchMap((response: any) => {
          console.log('Schema sent to backend successfully:', response);
          return this.tablesService.getTable(this.tableId!);
        })
      )
      .subscribe({
        next: () => {
          if (this.schemaSheetsConfig) {
            this.schemaConfig = {
              ...(this.schemaConfig ?? {}),
              configuration: {
                ...(this.schemaConfig?.configuration ?? {}),
                sheets: this.schemaSheetsConfig
              }
            };
          }
          this.schemaFormatUnsupported = false;

          if (this.generatedSchema) {
            const dto: any = { json_schema: this.generatedSchema };
            const configuration: any = {};
            if (this.schemaSettings) {
              configuration.require_provenance = this.schemaSettings.requireProvenance;
              configuration.fast_conversion = this.schemaSettings.fastConversion;
              configuration.multiple_rows = this.schemaSettings.multiple_rows;
              configuration.schema_splitting_threshold = this.schemaSettings.schemaSplittingThreshold;
              configuration.n_shot = this.schemaSettings.n_shot;
            }
            if (this.schemaSheetsConfig) {
              configuration.sheets = this.schemaSheetsConfig;
            }
            if (Object.keys(configuration).length) {
              dto.configuration = configuration;
            }
            const saved = JSON.stringify(dto, null, 2);
            this.lastReceivedSchema = saved;
            this.jsonSchemaChanged.emit(saved);
          }
        },
        error: (error: any) => {
          console.error('Failed to save schema to backend:', error);
          let errorMessage = 'Failed to save schema';
          if (error.error?.message) {
            errorMessage += `: ${error.error.message}`;
          } else if (error.message) {
            errorMessage += `: ${error.message}`;
          }
          this.toastService.showError('Error', errorMessage);
        }
      });
  }

  onColumnDrop(event: CdkDragDrop<any[]>, sheetIndex: number) {
    if (!this.isDragDropEnabled) {
      return;
    }

    this.stopAutoScroll();

    const propertiesArray = this.getSheetProperties(sheetIndex);
    const sheet = this.sheets.at(sheetIndex);
    const sheetName = sheet.get('name')?.value;

    // Perform the move operation in the form array
    moveItemInArray(propertiesArray.controls, event.previousIndex, event.currentIndex);

    // Get the updated column order
    const columns: ColumnDefinition[] = propertiesArray.controls.map(control => ({
      name: control.get('name')?.value,
      type: control.get('type')?.value,
      description: control.get('description')?.value,
      example: control.get('example')?.value,
      required: control.get('required')?.value,
      options: control.get('options')?.value ?? [],
      presence: control.get('presence')?.value ?? 'occasional'
    }));

    // Update the form validity
    propertiesArray.updateValueAndValidity();
    this.schemaForm.updateValueAndValidity();

    // If we have a schema ID, update the column order in the backend
    if (this.schema_id && sheetName) {
      this.schemaService.reorderColumns(this.schema_id, sheetName, columns).subscribe({
        next: () => {
          // Don't call saveAndGenerate() here as it will overwrite the backend changes
          // The backend already has the correct order
          this.toastService.show('info', 'Column order updated successfully', {
            classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
          });
        },
        error: (error) => {
          // Revert the move operation if the backend update fails
          moveItemInArray(propertiesArray.controls, event.currentIndex, event.previousIndex);
          this.toastService.showError('Error', 'Failed to update column order');
        }
      });
    } else {
      this.saveAndGenerate();
    }
  }

  onDragStarted() {
    this.startAutoScroll();
  }

  onDragEnded() {
    this.stopAutoScroll();
  }

  onDragMoved(event: any) {
    if (this.scrollableContainer && this.scrollableContainer.getElementRef()) {
      const containerElement = this.scrollableContainer.getElementRef().nativeElement;
      const containerRect = containerElement.getBoundingClientRect();
      const mouseY = event.pointerPosition.y;
      
      const relativeY = mouseY - containerRect.top;
      
      if (relativeY < this.SCROLL_ZONE && containerElement.scrollTop > 0) {
        this.autoScrollDirection = -1;
      } else if (relativeY > (containerRect.height - this.SCROLL_ZONE) && 
                 containerElement.scrollTop < (containerElement.scrollHeight - containerElement.clientHeight)) {
        this.autoScrollDirection = 1;
      } else {
        this.autoScrollDirection = 0;
      }
    }
  }

  private autoScrollDirection = 0;

  private startAutoScroll() {
    this.stopAutoScroll(); 
    
    this.autoScrollInterval = setInterval(() => {
      if (this.autoScrollDirection !== 0 && this.scrollableContainer) {
        const containerElement = this.scrollableContainer.getElementRef().nativeElement;
        const scrollAmount = this.SCROLL_SPEED * this.autoScrollDirection;
        containerElement.scrollTop += scrollAmount;
      }
    }, 16); // ~60fps
  }

  private stopAutoScroll() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
    this.autoScrollDirection = 0;
  }

  saveTemplate() {
    if (!this.generatedSchema) {
      this.toastService.showError('Error', 'No schema to save. Please create a template first.');
      return;
    }

    const currentSchemaName = this.schema_name || this.schemaForm.get('title')?.value || 'New Template';
    this.saveTemplateForm.patchValue({
      name: currentSchemaName
    });

    this.modalService.open(this.saveTemplateModal, { centered: true }).result.then(
      (result) => {
        if (result && result.name) {
          // Create a new schema in the database
          this.schemaService.createSchema({
            user_id: this.userService.userInfo.userId || '',
            schema_name: result.name,
            schema_content: this.generatedSchema,
            table_id: this.tableId || '',
            schema_config: this.schemaConfig
          }).subscribe({
            next: (createdSchema: SchemaResponse) => {
              // Update the current component's schema ID to the newly created one
              this.schema_id = createdSchema.schema_id;
              this.schema_name = createdSchema.schema_name;
              this.selectedTemplate = createdSchema.schema_id;

              // Apply the schema to the current dataspace
              if (this.tableId) {
                // First update the table's schema ID
                this.tablesService.updateTable(this.tableId, { schema_id: createdSchema.schema_id }).subscribe({
                  next: () => {
                    // Then apply the schema to the dataspace
                    this.tablesService.addSchemaToDataspace(
                      this.generatedSchema,
                      this.tableId!,
                      createdSchema.schema_id,
                      this.schemaSettings,
                      this.schemaSheetsConfig
                    ).subscribe({
                      next: (response) => {
                        console.log('Schema applied successfully:', response);
                        this.toastService.show('info', `Schema "${result.name}" saved and applied successfully`, {
                          classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
                        });
                        // Refresh the available schemas list
                        this.loadAvailableSchemas();
                      },
                      error: (error) => {
                        console.error('Failed to apply schema to dataspace:', error);
                        let errorMessage = 'Schema saved but failed to apply to dataspace';
                        if (error.error?.message) {
                          errorMessage += `: ${error.error.message}`;
                        } else if (error.message) {
                          errorMessage += `: ${error.message}`;
                        }
                        this.toastService.showError('Warning', errorMessage);
                        // Still refresh the schemas list since saving was successful
                        this.loadAvailableSchemas();
                      }
                    });
                  },
                  error: (error) => {
                    console.error('Failed to update table schema ID:', error);
                    let errorMessage = 'Schema saved but failed to update table schema ID';
                    if (error.error?.message) {
                      errorMessage += `: ${error.error.message}`;
                    } else if (error.message) {
                      errorMessage += `: ${error.message}`;
                    }
                    this.toastService.showError('Warning', errorMessage);
                    // Still refresh the schemas list since saving was successful
                    this.loadAvailableSchemas();
                  }
                });
              } else {
                // No table ID, just show success for saving
                this.toastService.show('info', `Schema "${result.name}" saved successfully`, {
                  classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
                });
                this.loadAvailableSchemas();
              }
            },
            error: (error) => {
              console.error('Failed to save schema:', error);
              let errorMessage = 'Failed to save schema';
              if (error.error?.message) {
                errorMessage += `: ${error.error.message}`;
              } else if (error.message) {
                errorMessage += `: ${error.message}`;
              }
              this.toastService.showError('Error', errorMessage);
            }
          });
        }
      },
      () => {} // Dismissed
    );
  }

  getCurrentSheetMultipleRows(): boolean {
    const sheet = this.sheets.at(this.activeTab);
    if (!sheet) return false;
    return !!sheet.get('multiple_rows')?.value;
  }

  private applyTopLevelMultipleRowsToAllSheets(): void {
    const value = this.schemaSettings?.multiple_rows ?? false;
    this.sheets.controls.forEach((sheet: AbstractControl) => {
      const group = sheet as FormGroup;
      const ctrl = group.get('multiple_rows');
      if (ctrl) {
        ctrl.setValue(value);
      } else {
        group.addControl('multiple_rows', this.fb.control(value));
      }
    });
    this.saveAndGenerate();
  }

  onMultipleRowsToggle(event: Event): void {
    const input = event.target as HTMLInputElement;
    const checked = input?.checked ?? false;
    const sheet = this.sheets.at(this.activeTab) as FormGroup;
    if (sheet) {
      const multipleRowsCtrl = sheet.get('multiple_rows');
      if (multipleRowsCtrl) {
        multipleRowsCtrl.setValue(checked);
      } else {
        sheet.addControl('multiple_rows', this.fb.control(checked));
      }
      this.saveAndGenerate();
    }
  }

  onTemplateChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    const schemaId = select.value;

    if (!schemaId) {
      return;
    }

    // Find the schema from available schemas
    const schema = this.availableSchemas.find(s => s.schema_id === schemaId);
    if (schema) {
      this.schema_id = schema.schema_id; // Update schema ID
      this.schema_name = schema.schema_name;
      this.initializeFormFromSchema(schema.schema_content);

      if (schema.schema_content) {
        const formattedSchema = JSON.stringify(schema.schema_content, null, 2);
        this.lastReceivedSchema = formattedSchema;
        this.jsonSchemaChanged.emit(formattedSchema);
      }

      if (this.tableId) {
        this.tablesService.updateTable(this.tableId, { schema_id: schema.schema_id }).subscribe({
          next: () => {
            if (schema.schema_content) {
              this.tablesService.addSchemaToDataspace(
                schema.schema_content,
                this.tableId!,
                schema.schema_id,
                this.schemaSettings,
                this.schemaSheetsConfig
              ).subscribe({
                next: (response) => {
                  console.log('Schema applied successfully:', response);
                  this.toastService.show('info', `Schema "${schema.schema_name}" applied successfully`, {
                    classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
                  });
                },
                error: (error) => {
                  console.error('Failed to apply schema to dataspace:', error);
                  let errorMessage = 'Failed to apply schema to dataspace';
                  if (error.error?.message) {
                    errorMessage += `: ${error.error.message}`;
                  } else if (error.message) {
                    errorMessage += `: ${error.message}`;
                  }
                  this.toastService.showError('Error', errorMessage);
                }
              });
            } else {
              this.toastService.showError('Error', 'Schema content is empty');
            }
          },
          error: (error) => {
            console.error('Failed to update table schema ID:', error);
            let errorMessage = 'Failed to update table schema ID';
            if (error.error?.message) {
              errorMessage += `: ${error.error.message}`;
            } else if (error.message) {
              errorMessage += `: ${error.message}`;
            }
            this.toastService.showError('Error', errorMessage);
          }
        });
      } else {
        this.toastService.showError('Error', 'No table ID available');
      }
    } else {
      this.toastService.showError('Error', 'Schema not found');
    }
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private loadAvailableSchemas() {
    this.schemaService.getAllSchemas().subscribe({
      next: (schemas) => {
        this.availableSchemas = schemas.sort((a, b) => b.updated_at - a.updated_at);
      },
      error: (error) => {
        console.error('Failed to load schemas:', error);
        this.toastService.showError('Error', 'Failed to load available schemas');
      }
    });
  }

  getSelectedOptionText(): string {
    if (!this.selectedTemplate) return '';

    const schema = this.availableSchemas.find(s => s.schema_id === this.selectedTemplate);
    if (schema) return schema.schema_name;

    return '';
  }

  onTemplateSelect(id: string) {
    this.selectedTemplate = id;
    this.onTemplateChange({ target: { value: id } } as any);
    if (this.templateDropdown) {
      this.templateDropdown.close();
    }
  }

  deleteSchema(id: string, event: Event) {
    event.stopPropagation();
    const schema = this.availableSchemas.find(s => s.schema_id === id);
    if (!schema) return;

    this.deleteType = 'Structure';
    this.deleteItemName = schema.schema_name;
    this.deleteItemId = schema.schema_id;
    this.deleteCallback = () => {
      this.schemaService.deleteSchema(id).subscribe({
        next: () => {
          this.availableSchemas = this.availableSchemas.filter(s => s.schema_id !== id);
          this.toastService.show('info', `Schema "${schema.schema_name}" deleted successfully`, {
            classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
          });

          if (this.selectedTemplate === id) {
            this.selectedTemplate = '';
            this.initializeEmptyForm();
          }

          if (this.templateDropdown) {
            this.templateDropdown.close();
          }
        },
        error: (error) => {
          this.toastService.showError('Error', 'Failed to delete schema');
        }
      });
    };

    this.showDeleteConfirmation();
  }

  private showDeleteConfirmation() {
    if (this.templateDropdown) {
      this.templateDropdown.close();
    }

    this.modalService.open(this.deleteConfirmModal, { centered: true }).result.then(
      (result) => {
        if (result === 'delete' && this.deleteCallback) {
          this.deleteCallback();
        }
      },
      () => {} // Dismissed
    );
  }

  private isValidSchema(schema: any): boolean {
    try {
      if (!schema || typeof schema !== 'object') {
        console.warn('Schema is not an object, but continuing with form validation');
        return true; 
      }

      if (!schema.properties || !schema.properties.sheets) {
        console.warn('Schema missing sheets property, but continuing with form validation');
        return true; 
      }

      if (!Array.isArray(schema.properties.sheets.items)) {
        console.warn('Schema sheets.items is not an array, but continuing with form validation');
        return true; 
      }

      if (schema.properties.sheets.items.length === 0) {
        console.warn('Schema has no sheet definitions, but continuing with form validation');
        return true;
      }

      for (const sheetDef of schema.properties.sheets.items) {
        if (!sheetDef.properties || !sheetDef.properties.sheet_name || !sheetDef.properties.rows) {
          console.warn('Invalid sheet definition found, but continuing with form validation:', sheetDef);
          return true; 
        }
      }

      if (!schema.$defs || Object.keys(schema.$defs).length === 0) {
        console.warn('Schema missing $defs, but continuing with form validation');
        return true; 
      }

      return true;
    } catch (error) {
      console.error('Error validating schema, but continuing with form validation:', error);
      return true; 
    }
  }

  editTemplate(id: string, event: Event) {
    event.stopPropagation();
    const schema = this.availableSchemas.find(s => s.schema_id === id);
    if (!schema) return;

    this.editingTemplateId = id;
    this.editingTemplateName = schema.schema_name;

    setTimeout(() => {
      const input = document.querySelector(`input[data-template-id="${id}"]`) as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  saveTemplateEdit(id: string, newName: string) {
    if (!newName.trim() || this.isSavingTemplate) {
      this.cancelTemplateEdit();
      return;
    }

    const schema = this.availableSchemas.find(s => s.schema_id === id);
    if (!schema) {
      this.cancelTemplateEdit();
      return;
    }

    this.isSavingTemplate = true;

    this.schemaService.updateSchema(id, {
      schema_content: schema.schema_content,
      schema_name: newName.trim()
    }).subscribe({
      next: (updatedSchema) => {
        const schemaIndex = this.availableSchemas.findIndex(s => s.schema_id === id);
        if (schemaIndex !== -1) {
          this.availableSchemas[schemaIndex].schema_name = newName.trim();
        }

        if (this.selectedTemplate === id) {
          this.schema_name = newName.trim();
        }

        this.cancelTemplateEdit();
        this.toastService.show('info', `Template renamed to "${newName.trim()}" successfully`, {
          classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
        });
        this.isSavingTemplate = false;
      },
      error: (error) => {
        console.error('Failed to update template name:', error);
        this.toastService.showError('Error', 'Failed to update template name');
        this.cancelTemplateEdit();
        this.isSavingTemplate = false;
      }
    });
  }

  cancelTemplateEdit() {
    this.editingTemplateId = '';
    this.editingTemplateName = '';
    this.isSavingTemplate = false;
  }

  onTemplateNameKeydown(event: KeyboardEvent, id: string) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const input = event.target as HTMLInputElement;
      this.saveTemplateEdit(id, input.value);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelTemplateEdit();
    }
  }

  onTemplateNameBlur(event: FocusEvent, id: string) {

    if (!this.isSavingTemplate) {
      const input = event.target as HTMLInputElement;
      this.saveTemplateEdit(id, input.value);
    }
  }

  onTemplateNameInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.editingTemplateName = input.value;
  }

  sendPromptToAI(): void {
    console.log(this.schemaForm.value.prompt);
    this.showTextInputForAI = false;
  }

  openCardEditModal(sheet: AbstractControl, column?: AbstractControl): void {
    if (this.isCardEditModalOpen) {
      return;
    }
    this.isCardEditModalOpen = true;
    this.renderer.addClass(this.document.body, 'card-edit-open');
    const modalRef = this.modalService.open(CardEditComponent, {
      windowClass: 'card-edit-modal',
      backdropClass: 'card-edit-backdrop'
    });
    modalRef.componentInstance.sheet = sheet;
    if (column) {
      modalRef.componentInstance.column = column;
    }
    modalRef.result
      .then((result) => {
        if (result) {
          if (column) {
            this.updateColumnFromModal(result, sheet, column);
          } else {
            this.addColumnFromModal(result, sheet);
          }
        }
      })
      .finally(() => {
        this.renderer.removeClass(this.document.body, 'card-edit-open');
        this.isCardEditModalOpen = false;
      });
  }

  private addColumnFromModal(modalData: any, sheet: AbstractControl): void {
    const isEnum = modalData?.dataType === 'enum';
    const enumValues = Array.isArray(modalData?.enumValues) ? modalData.enumValues : [];
    const hasEnumValues = enumValues.length > 0;
    const hasExample =
      modalData?.example !== undefined && modalData?.example !== null && modalData?.example !== '';
    const columnData: ColumnDefinition = {
      name: (modalData?.name ?? '').trim(),
      type: isEnum ? 'string' : (modalData?.dataType ?? 'string'),
      description: modalData?.description ?? '',
      ...(!hasEnumValues && hasExample ? { example: modalData?.example ?? '' } : {}),
      required: modalData?.presence === 'common',
      ...(hasEnumValues ? { options: enumValues } : {}),
      presence: modalData?.presence ?? 'occasional'
    };

    const sheetIndex = this.sheets.controls.indexOf(sheet);
    if (sheetIndex !== -1) {
      this.activeTab = sheetIndex;
    }

    this.addColumn(columnData, sheet);
  }

  private updateColumnFromModal(modalData: any, sheet: AbstractControl, column: AbstractControl): void {
    const isEnum = modalData?.dataType === 'enum';
    const enumValues = Array.isArray(modalData?.enumValues) ? modalData.enumValues : [];
    const hasEnumValues = enumValues.length > 0;
    const hasExample =
      modalData?.example !== undefined && modalData?.example !== null && modalData?.example !== '';
    const columnData: ColumnDefinition = {
      name: (modalData?.name ?? '').trim(),
      type: isEnum ? 'string' : (modalData?.dataType ?? 'string'),
      description: modalData?.description ?? '',
      ...(!hasEnumValues && hasExample ? { example: modalData?.example ?? '' } : {}),
      required: modalData?.presence === 'common',
      ...(hasEnumValues ? { options: enumValues } : {}),
      presence: modalData?.presence ?? 'occasional'
    };

    const sheetName = sheet.get('name')?.value;
    const oldColumnName = column.get('oldName')?.value || column.get('name')?.value;

    const columnGroup = column as FormGroup;
    columnGroup.patchValue({
      ...columnData,
      isEditing: false,
      oldName: columnData.name
    });
    if (hasEnumValues) {
      if (columnGroup.get('example')) {
        (columnGroup as FormGroup<any>).removeControl('example');
      }
    } else {
      if (!columnGroup.get('example')) {
        (columnGroup as FormGroup<any>).addControl('example', new FormControl(columnData.example ?? ''));
      } else {
        columnGroup.get('example')?.setValue(columnData.example ?? '');
      }
    }
    if (hasEnumValues) {
      if (!columnGroup.get('options')) {
        (columnGroup as FormGroup<any>).addControl('options', new FormControl(enumValues));
      } else {
        columnGroup.get('options')?.setValue(enumValues);
      }
    } else if (columnGroup.get('options')) {
      (columnGroup as FormGroup<any>).removeControl('options');
    }

    if (this.schema_id && sheetName) {
      this.schemaService.updateColumn(this.schema_id, sheetName, oldColumnName, columnData).subscribe({
        next: () => {
          this.saveAndGenerate();
          this.toastService.show('info', 'Column updated successfully', {
            classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
          });
        },
        error: () => {
          this.toastService.showError('Error', 'Failed to update column');
        }
      });
    } else {
      this.saveAndGenerate();
    }
  }
}
